import React from "react";
import {
  Dimensions,
  KeyboardAvoidingView,
  Modal,
  Platform,
  StyleSheet,
  TouchableOpacity,
  View,
  Animated,
  Keyboard,
} from "react-native";
import { useSelector } from "react-redux";
import Width from "../assets/Width";
import Loader from "./Loader/Loader";
import FastImage from "../common/FastImage";
import { close } from "../utils/images";
import { Color } from "../assets";
import { isMobile } from "../utils/commonMethods";
import { globalConfigurableData } from "../utils/globalConstant";

const CommonModal = ({
  showModal,
  children,
  parentStyles = null,
  customStyles = null,
  onModalCallBack = () => { },
  customModalStyle = null,
  animationType = "slide",
  persistent = false
}) => {
  const loader = useSelector(({ LoaderReducer }) => LoaderReducer?.loader);
  const fadeAnim = React.useRef(new Animated.Value(0)).current;
  const slideAnim = React.useRef(new Animated.Value(Width.height)).current;
  const scaleAnim = React.useRef(new Animated.Value(0.3)).current;
  const [isVisible, setIsVisible] = React.useState(showModal);
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [keyboardHeight, setKeyboardHeight] = React.useState(0);
  const isMobileView = isMobile();
  const isIOS = Platform.OS === 'ios';

  React.useEffect(() => {
    if (isIOS) {
      const keyboardWillShow = Keyboard.addListener('keyboardWillShow', (e) => {
        setKeyboardHeight(e.endCoordinates.height);
      });
      const keyboardWillHide = Keyboard.addListener('keyboardWillHide', () => {
        setKeyboardHeight(0);
      });

      return () => {
        keyboardWillShow.remove();
        keyboardWillHide.remove();
      };
    }
  }, []);

  const animate = React.useCallback((show) => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    
    if (show) {
      slideAnim.setValue(Width.height);
      scaleAnim.setValue(0.3);
      fadeAnim.setValue(0);
    }

    const animations = [
      Animated.timing(fadeAnim, {
        toValue: show ? 1 : 0,
        duration: 200,
        useNativeDriver: true,
      })
    ];

    if (isMobileView) {
      animations.push(
        show ? 
          Animated.spring(slideAnim, {
            toValue: 0,
            tension: 65,
            friction: 11,
            useNativeDriver: true,
          }) :
          Animated.timing(slideAnim, {
            toValue: Width.height,
            duration: 200,
            useNativeDriver: true,
          })
      );
    } else {
      animations.push(
        Animated.spring(scaleAnim, {
          toValue: show ? 1 : 0.3,
          tension: 65,
          friction: 8,
          useNativeDriver: true,
        })
      );
    }

    Animated.parallel(animations).start(({ finished }) => {
      setIsAnimating(false);
      if (!show && finished) {
        setIsVisible(false);
        onModalCallBack();
      }
    });
  }, [fadeAnim, slideAnim, scaleAnim, isAnimating, isMobileView, onModalCallBack]);

  const handleClose = React.useCallback(() => {
    if (!loader && !isAnimating && !persistent) {
      animate(false);
    }
  }, [loader, isAnimating, persistent, animate]);

  React.useEffect(() => {
    if (showModal && !isVisible) {
      setIsVisible(true);
      requestAnimationFrame(() => {
        animate(true);
      });
    } else if (!showModal && isVisible && !isAnimating) {
      animate(false);
    }
  }, [showModal, isVisible, isAnimating, animate]);

  const modalContent = (
    <Animated.View
      style={[
        styles.modalView,
        customModalStyle,
        !isMobileView && styles.desktopModalView,
        isMobileView ? 
          animationType === "slide" && {
            transform: [{ translateY: slideAnim }]
          } : {
            transform: [
              { scale: scaleAnim }
            ]
          },
        isIOS && {
          paddingBottom: keyboardHeight > 0 ? keyboardHeight : 16
        }
      ]}
    >
      {isMobileView && !persistent && (
        <View style={styles.closeButtonContainer}>
          <TouchableOpacity
            style={styles.closeButton}
            onPress={handleClose}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <FastImage style={styles.closeIcon} source={globalConfigurableData?.close()} />
          </TouchableOpacity>
        </View>
      )}
      <TouchableOpacity
        onPress={(e) => e.stopPropagation()}
        activeOpacity={1}
        style={[
          { width: '100%' },
          !isMobileView && styles.desktopContentContainer
        ]}
      >
        {children}
      </TouchableOpacity>
    </Animated.View>
  );

  if (!isVisible && !showModal) return null;

  const ModalContent = isIOS ? (
    <KeyboardAvoidingView
      behavior="padding"
      style={[styles.keyboardAvoidingView]}
      keyboardVerticalOffset={0}
    >
      {modalContent}
    </KeyboardAvoidingView>
  ) : modalContent;

  return (
    <Modal
      animationType="none"
      transparent
      visible={isVisible}
      onRequestClose={handleClose}
    >
      <Animated.View
        style={[
          styles.modalContainer,
          parentStyles,
          { opacity: fadeAnim }
        ]}
      >
        <TouchableOpacity
          style={[
            styles.modalContainer,
            !isMobileView && styles.desktopModalContainer
          ]}
          activeOpacity={1}
          onPress={handleClose}
        >
          {Platform.OS === "android" ? (
            ModalContent
          ) : (
            ModalContent
          )}
        </TouchableOpacity>
      </Animated.View>
      <Loader />
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalContainer: {
    backgroundColor: "rgba(23, 23, 23, 0.6)",
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    width: Width.width,
    margin: "auto",
  },
  keyboardAvoidingView: {
    width: '100%',
    flex: 1,
    justifyContent: 'center',
  },
  desktopModalContainer: {
    maxWidth: '100%',
    alignItems: 'center',
  },
  modalView: {
    backgroundColor: "white",
    padding: 16,
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 6,
    width: '100%',
  },
  desktopModalView: {
    width: 'auto',
    minWidth: 400,
    maxWidth: '90%',
    borderRadius: 8,
  },
  desktopContentContainer: {
    width: 'auto',
    minWidth: 400,
    maxWidth: '100%',
  },
  closeButtonContainer: {
    position: 'absolute',
    right: 15,
    top: -70,
    zIndex: 999,
    elevation: 7,
  },
  closeButton: {
    padding: 8,
    borderRadius: 100,
    backgroundColor: globalConfigurableData.globalThemeColor,
  },
  closeIcon: {
    width: 25,
    height: 25,
  }
});

export default CommonModal;
